import { CURRENT_CLIENT } from "@/constants";
import type { ClientTheme } from "@/types/theme";

interface ClientBrandProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  headerOnPrimary?: boolean;
}

function hasBrandFont(): boolean {
  return Boolean((CURRENT_CLIENT as { theme?: ClientTheme }).theme?.typography?.fontFamily?.brand);
}

/** `font-brand font-normal` when tenant configures `theme.typography.fontFamily.brand`. */
export function tenantBrandTypographyClass(fallback = 'font-semibold'): string {
  return hasBrandFont() ? 'font-brand font-normal' : fallback
}

/**
 * Generic brand component that works for all clients.
 * Supports custom brand styling via theme config (brandStyle, brandPattern).
 */
export const ClientBrand = ({ className = "", size = 'md', headerOnPrimary = false }: ClientBrandProps) => {
  const brandStyle = (CURRENT_CLIENT as any).brandStyle;
  const brandPattern = (CURRENT_CLIENT as any).brandPattern as { boxed?: string; suffix?: string } | undefined;

  const hasCustomBrandStyle = brandStyle === "boxed";

  const letterBoxClasses = headerOnPrimary
    ? "bg-white dark:bg-gray-900 text-primary-dark dark:text-primary"
    : "bg-primary text-white dark:text-gray-900";
  const coTextClasses = headerOnPrimary
    ? "text-white dark:text-gray-900"
    : "text-primary";
  
  if (hasCustomBrandStyle) {
    const sizeClasses = {
      sm: {
        letter: "w-3 h-5 text-sm font-semibold font-mono",
        co: "font-semibold text-sm"
      },
      md: {
        letter: "w-3.5 h-6 text-base font-semibold font-mono",
        co: "font-semibold text-base"
      },
      lg: {
        letter: "w-4 h-7 text-lg font-semibold font-mono",
        co: "font-semibold text-lg"
      },
      xl: {
        letter: "w-5 h-8 text-xl font-semibold font-mono",
        co: "font-semibold text-xl"
      },
      '2xl': {
        letter: "w-6 h-10 text-2xl font-semibold font-mono",
        co: "font-semibold text-2xl"
      },
      '3xl': {
        letter: "w-7 h-12 text-3xl font-semibold font-mono",
        co: "font-semibold text-3xl"
      }
    };

    const currentSize = sizeClasses[size];
    const brandName = CURRENT_CLIENT.brand || CURRENT_CLIENT.name;

    // Use brandPattern from config when available (e.g. boxed: "Talent", suffix: "Co")
    if (brandPattern?.boxed != null && brandPattern?.suffix != null) {
      const boxedLetters = brandPattern.boxed.split("");
      const suffixPart = brandPattern.suffix;

      return (
        <div className={`flex items-baseline gap-0 ${className}`}>
          {boxedLetters.map((letter, index) => (
            <span
              key={index}
              className={`${currentSize.letter} ${letterBoxClasses} flex items-center justify-center`}
            >
              {letter}
            </span>
          ))}
          <span className={`${currentSize.co} ${coTextClasses} ml-0.5`}>
            {suffixPart}
          </span>
        </div>
      );
    }

    const letters = brandName.split("").slice(0, 6);
    return (
      <div className={`flex items-baseline gap-0 ${className}`}>
        {letters.map((letter, index) => (
          <span
            key={index}
            className={`${currentSize.letter} ${letterBoxClasses} flex items-center justify-center`}
          >
            {letter}
          </span>
        ))}
        {brandName.length > letters.length && (
          <span 
            className={`${currentSize.co} ${coTextClasses} ml-0.5`}
          >
            {brandName.slice(letters.length)}
          </span>
        )}
      </div>
    );
  }

  // Default: simple text brand
  const sizeClasses = {
    sm: "font-semibold text-sm",
    md: "font-semibold text-base",
    lg: "font-semibold text-lg",
    xl: "font-semibold text-xl",
    '2xl': "font-semibold text-2xl",
    '3xl': "font-semibold text-3xl"
  };

  return (
    <span className={`truncate ${tenantBrandTypographyClass()} ${sizeClasses[size]} ${className}`}>
      {CURRENT_CLIENT.brand}
    </span>
  );
};

/**
 * Matrix-style Animated Brand for use in animated titles.
 * Supports custom brand styling via theme config.
 */
export const MatrixClientBrand = ({ text, className = "", size = 'md', headerOnPrimary = false }: ClientBrandProps & { text: string }) => {
  const brandStyle = (CURRENT_CLIENT as any).brandStyle;  const brandPattern = (CURRENT_CLIENT as any).brandPattern as { boxed?: string; suffix?: string } | undefined;

  const hasCustomBrandStyle = brandStyle === "boxed";
  
  const letterBoxClasses = headerOnPrimary
    ? "bg-white dark:bg-gray-900 text-primary-dark dark:text-primary"
    : "bg-primary text-white dark:text-gray-900";
  const coTextClasses = headerOnPrimary
    ? "text-white dark:text-gray-900"
    : "text-primary";
  
  if (!hasCustomBrandStyle) {
    const sizeClasses = {
      sm: "text-sm",
      md: "text-base",
      lg: "text-lg",
      xl: "text-xl",
      '2xl': "text-2xl",
      '3xl': "text-3xl"
    };

    return (
      <span className={`truncate ${tenantBrandTypographyClass()} ${sizeClasses[size]} ${className}`}>
        {text}
      </span>
    );
  }

  const sizeClasses = {
    sm: {
      letter: "w-3 h-5 text-sm font-semibold font-mono",
      co: "font-semibold text-sm",
      normal: "font-semibold text-sm"
    },
    md: {
      letter: "w-3.5 h-6 text-base font-semibold font-mono",
      co: "font-semibold text-base",
      normal: "font-semibold text-base"
    },
    lg: {
      letter: "w-4 h-7 text-lg font-semibold font-mono",
      co: "font-semibold text-lg",
      normal: "font-semibold text-lg"
    },
    xl: {
      letter: "w-5 h-8 text-xl font-semibold font-mono",
      co: "font-semibold text-xl",
      normal: "font-semibold text-xl"
    },
    '2xl': {
      letter: "w-6 h-10 text-2xl font-semibold font-mono",
      co: "font-semibold text-2xl",
      normal: "font-semibold text-2xl"
    },
    '3xl': {
      letter: "w-7 h-12 text-3xl font-semibold font-mono",
      co: "font-semibold text-3xl",
      normal: "font-semibold text-3xl"
    }
  };

  const currentSize = sizeClasses[size];

  // Config-driven boxed brand with brandPattern (e.g. boxed: "Talent", suffix: "Co")
  if (brandPattern?.boxed != null && brandPattern?.suffix != null) {
    const targetLetters = brandPattern.boxed.split("");
    const suffixChars = brandPattern.suffix.split("");
    const extraChars = "!".split(""); // Common suffix for title variations
    const coLetters = [...suffixChars, ...extraChars];
    const fullBrand = brandPattern.boxed + brandPattern.suffix;
    const textArray = text.split("");

    // Final state: exact brand match (e.g. "Acme")
    if (text === fullBrand) {
      return (
        <div className={`flex items-baseline gap-0 ${className}`}>
          {targetLetters.map((letter, index) => (
            <span
              key={index}
              className={`${currentSize.letter} ${letterBoxClasses} flex items-center justify-center`}
            >
              {letter}
            </span>
          ))}
          <span className={`${currentSize.co} ${coTextClasses} ml-0.5`}>
            {brandPattern.suffix}
          </span>
        </div>
      );
    }

    // Final state: brand + ! (e.g. "Acme!")
    if (text === fullBrand + "!") {
      return (
        <div className={`flex items-baseline gap-0 ${className}`}>
          {targetLetters.map((letter, index) => (
            <span
              key={index}
              className={`${currentSize.letter} ${letterBoxClasses} flex items-center justify-center`}
            >
              {letter}
            </span>
          ))}
          <span className={`${currentSize.co} ${coTextClasses} ml-0.5`}>
            {brandPattern.suffix}
          </span>
          <span className={`${currentSize.co} ${coTextClasses}`}>!</span>
        </div>
      );
    }

    // Matrix animation handling
    return (
      <div className={`flex items-baseline gap-0 ${className}`}>
        {textArray.map((char, index) => {
          if (char === " ") {
            return <span key={index}>&nbsp;</span>;
          }

          const isBoxedLetter = targetLetters.includes(char);
          const shouldStyleAsBoxed =
            isBoxedLetter && index < targetLetters.length && char === targetLetters[index];

          if (shouldStyleAsBoxed) {
            return (
              <span
                key={index}
                className={`${currentSize.letter} ${letterBoxClasses} flex items-center justify-center`}
              >
                {char}
              </span>
            );
          }

          const isSuffixLetter = coLetters.includes(char);
          if (isSuffixLetter) {
            return (
              <span key={index} className={`${currentSize.normal} ${coTextClasses}`}>
                {char}
              </span>
            );
          }

          return (
            <span key={index} className={currentSize.normal}>
              {char}
            </span>
          );
        })}
      </div>
    );
  }

  // Generic fallback
  return (
    <span className={`truncate ${sizeClasses[size]} ${className}`}>
      {text}
    </span>
  );
};
